import { User } from '@auth0/auth0-react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';

import { Auth0AppUser, CustomPermission, AppUser, QueryOption, LoggedInUser } from '../@types';
import { readUploadedProfilePicUrl } from './readUploadedProfilePicUrl';
import {
  createClientAuthSide,
  deleteAuthUserById,
  getAuthUserById,
  sendWelcomeEmail,
  updateAuthUserById,
} from '../api/auth';
import { uploadUserProfileImage } from '../api/uploadedFiles';
import {
  getUsers,
  getUserById,
  createUser,
  updateUser,
  updateUserEmailVerification,
  getUserPermissions,
  createUserPermissions,
  createUserWhenLogin,
  getUserByAuth0Id,
  getUserSystemPermissions,
  getLoggedInUserData,
  updateProfileConfig,
} from '../api/users';
import type { UserListResponse } from '../api/users';
import { USER_OBJECT_KEY } from '../configs/envs';
import {
  AppUserPermissionKey,
  AppUserSystemPermissionKey,
  AppUsersGetOneKey,
  AppUsersKey,
  AuthAppUserKey,
} from '../configs/users';
import { ProfileConfig } from '../pages/Profile/Profile.props';
import { UserCreationError } from '../utils/user-creation-error';

export type { UserListResponse } from '../api/users';

// User creation data
interface UserCreateData extends Partial<AppUser> {
  profilePicDetails?: FileList;
}

// User update data - separate from AppUser because profilePic can be a FileList during update
interface UserUpdateData {
  id: number;
  profilePic?: FileList | string;
  firstName?: string;
  lastName?: string;
  email?: string;
  contactEmail?: string;
  auth0Id?: string;
  isActive?: boolean;
  isLocked?: boolean;
  isEmailVerified?: boolean;
  timezoneId?: number;
  notes?: string;
  systemRoleId?: number;
  structureId?: number;
}

// Auth0 update payload
interface Auth0UpdatePayload {
  id: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  blocked?: boolean;
  account_enabled?: boolean;
}

// Auth user from login provider
interface AuthUserFromProvider {
  sub?: string;
  email_verified?: boolean;
  given_name?: string;
  family_name?: string;
  name?: string;
  nickname?: string;
  email?: string;
  blocked?: boolean;
  picture?: string;
  updated_at?: string;
}

export const useGetAllUsers = (queryOption: QueryOption): UseQueryResult<UserListResponse> => {
  return useQuery({
    queryKey: [
      AppUsersKey,
      queryOption.page,
      queryOption.pageSize,
      queryOption.filterModel,
      queryOption.sortModel,
      queryOption.multiSearchQuery,
    ],
    queryFn: () => getUsers(queryOption),
  });
};

export const useGetOneUser = (id: number): UseQueryResult<AppUser> => {
  return useQuery({
    queryKey: [AppUsersGetOneKey, id],
    queryFn: () => getUserById(id),
    select: (response: AppUser) => response,
  });
};

// /users/{id} doesn't include the user's profile config (myZones,
// serviceAdvancedMode, etc.). The byAuth0Id endpoint does, so we use it as a
// secondary fetch on the user-edit page once we know the auth0Id.
const userProfileConfigQueryKey = (auth0Id: string | undefined) =>
  [AppUsersKey, 'profile-config', auth0Id ?? ''] as const;

export const useGetUserProfileConfig = (
  auth0Id: string | undefined
): UseQueryResult<ProfileConfig | undefined> => {
  return useQuery({
    queryKey: userProfileConfigQueryKey(auth0Id),
    queryFn: async () => {
      if (!auth0Id) return undefined;
      const user = await getUserByAuth0Id(auth0Id);
      return user.config;
    },
    enabled: !!auth0Id,
  });
};

/** Read + parse the stored Auth0 user object (the single source both the query
 *  key and the query itself derive from). */
const readStoredAuth0User = (): { sub: string } | null => {
  const userObj = localStorage.getItem(USER_OBJECT_KEY);
  return userObj ? (JSON.parse(userObj) as { sub: string }) : null;
};

const buildCurrentUserKey = (user: { sub: string } | null): string[] =>
  user ? [AppUsersKey, user.sub] : ['disabled'];

/** Query key for the current user, derived from the stored Auth0 subject. Shared
 *  so callers can read the freshest cached user/config from the query cache
 *  outside the hook (e.g. to merge a config write against the latest value). */
export const currentUserQueryKey = (): string[] => buildCurrentUserKey(readStoredAuth0User());

export const useGetCurrentUser = (): UseQueryResult<AppUser> => {
  const user = readStoredAuth0User();

  return useQuery({
    queryKey: buildCurrentUserKey(user),
    queryFn: async () => {
      if (!user) throw new Error('User object not found');
      return getUserByAuth0Id(user.sub);
    },
    enabled: !!user,
    staleTime: 60 * 60 * 1000,
  });
};

export const useCreateUser = (): UseMutationResult<{ id: number }, Error, UserCreateData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UserCreateData) => {
      const { email, firstName, lastName, profilePicDetails } = data;
      if (!email || !firstName || !lastName) {
        throw new Error('Email, first name, and last name are required');
      }
      const authResponse = await createClientAuthSide(email, firstName, lastName, '');
      const userData: Partial<AppUser> = {
        ...data,
        contactEmail: email,
        auth0Id: authResponse.user_id,
        // POST /users requires lastActive to be a non-empty, valid date (BFF
        // validates `.notEmpty()` and the column is NOT NULL), so an empty
        // string is rejected with a 400. Seed it with the Auth0 record's
        // updated_at (the just-created login's timestamp), falling back to now.
        lastActive: authResponse.updated_at || new Date().toISOString(),
        isActive: true,
        isLocked: false,
        isEmailVerified: false,
        structureId: 1,
      };

      // The Auth0 login now exists. If the DB-side record fails to save we'd be
      // left with an orphaned login, so roll it back and raise a typed error the
      // UI can surface instead of failing silently.
      let response;
      try {
        response = await createUser(userData);
      } catch (createError) {
        const orphanedAuth0Id = authResponse.user_id;
        let authRolledBack = true;
        try {
          if (orphanedAuth0Id) {
            await deleteAuthUserById(orphanedAuth0Id);
          }
        } catch (rollbackError) {
          authRolledBack = false;
          // Log the id only (no PII) so support can locate the orphaned login.
          console.error(
            // Id passed as an argument rather than interpolated: a non-literal
            // format string lets a crafted value forge log output.
            'Failed to roll back orphaned Auth0 login after user-record creation failed. Auth0 id:',
            orphanedAuth0Id,
            rollbackError
          );
        }
        throw new UserCreationError(
          createError instanceof Error ? createError.message : 'User record creation failed',
          authRolledBack,
          authRolledBack ? undefined : orphanedAuth0Id
        );
      }

      // Profile-image upload happens after the record exists; a failure here is
      // not rolled back (the user is already created) and propagates as-is.
      if (response?.id && profilePicDetails?.[0]) {
        const formData = new FormData();
        formData.append('fileList', profilePicDetails[0]);
        await uploadUserProfileImage(response.id, formData);
      }

      return response;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AppUsersKey],
      });
    },
  });
};

/** Resolves to the stored profile-pic URL when the save included one, so the
 *  caller can swap the temporary `blob:` preview for the real URL instead of
 *  holding a handle that dies with the document. */
export const useUpdateUser = (): UseMutationResult<string | undefined, Error, UserUpdateData> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UserUpdateData) => {
      const { profilePic, id, ...restData } = data;
      const updatedData: Partial<AppUser> = { ...restData };

      if (profilePic && typeof profilePic !== 'string') {
        const formData = new FormData();
        formData.append('fileList', profilePic[0]);
        const uploadedFileResponses = await uploadUserProfileImage(id, formData);
        // Throws a user-readable error when the response names no stored file,
        // rather than a TypeError on `[0].url` that the caller cannot explain.
        updatedData.profilePic = readUploadedProfilePicUrl(uploadedFileResponses);
      } else if (typeof profilePic === 'string') {
        updatedData.profilePic = profilePic;
      }

      await updateUser(id, updatedData);
      return updatedData.profilePic;
    },

    onSuccess: async (_uploadedPic, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [AppUsersKey] }),
        // The user-edit page reads /users/{id} through `useGetOneUser`, which is
        // keyed [AppUsersGetOneKey, id] — a different family from [AppUsersKey].
        // Without this the detail record stays stale after a save, so an
        // uploaded avatar was never reconciled from the server.
        queryClient.invalidateQueries({ queryKey: [AppUsersGetOneKey, variables.id] }),
      ]);
    },
  });
};

export const useGetUserPermissions = (id: string): UseQueryResult<CustomPermission[]> => {
  return useQuery({
    queryKey: [AppUserPermissionKey, id],
    queryFn: () => getUserPermissions(id),
  });
};

export const useGetUserSystemPermissions = (): UseQueryResult<CustomPermission[]> => {
  return useQuery({
    queryKey: [AppUserSystemPermissionKey],
    queryFn: async () => {
      const data = await getUserSystemPermissions();
      // The BFF returns {} (not []) when the user has no CMS2 record yet (new-user
      // race condition). Normalise to an empty array so callers can safely use
      // array methods (e.g. .some()) without crashing.
      return Array.isArray(data) ? data : [];
    },
    staleTime: 60 * 60 * 1000,
  });
};

export const useAddUserPermissions = (): UseMutationResult<
  void,
  Error,
  { id: string; customPermissions: CustomPermission[] }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: string; customPermissions: CustomPermission[] }) =>
      createUserPermissions(data.id, data.customPermissions),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AppUsersKey],
      });
    },
  });
};

export const useGetOneAuthUser = (id: string): UseQueryResult<Auth0AppUser | undefined> => {
  return useQuery({
    queryKey: [AuthAppUserKey, id],
    queryFn: () => getAuthUserById(id),
    // Gate on `enabled` rather than returning undefined from the queryFn for a
    // blank id: React Query v5 rejects an undefined resolution ("Query data
    // cannot be undefined"), so the old guard put the query into an *error*
    // state and logged on every render of a user whose auth0Id hadn't loaded
    // yet. Disabled, it simply stays pending, which is what a missing id means.
    enabled: !!id,
  });
};

export const useSendWelcomeEmail = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => await sendWelcomeEmail(id),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AuthAppUserKey],
      });
    },
  });
};

export const useUpdateAuth0User = (): UseMutationResult<void, Error, Auth0UpdatePayload> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Auth0UpdatePayload) => {
      const { id, email, name, given_name, family_name, blocked, account_enabled } = data;
      await updateAuthUserById(id, {
        email,
        name,
        given_name,
        family_name,
        blocked,
        account_enabled,
      });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AuthAppUserKey],
      });
    },
  });
};

export const useAuthUser = (
  user: User | AuthUserFromProvider | null | undefined
): UseMutationResult<void, Error, AuthUserFromProvider> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (authUser: AuthUserFromProvider) => {
      if (!authUser?.sub) return;

      const authUserResponse = await getUserByAuth0Id(authUser.sub).catch((error: Error) => {
        console.error('Error fetching user by Auth0 Id:', error);
        return undefined;
      });

      if (authUserResponse) {
        // trigger this when user's email verified from auth0 and not verified in DB
        if (authUser?.email_verified && !authUserResponse.isEmailVerified) {
          await updateUserEmailVerification(authUserResponse.id, { isEmailVerified: true });
        }
      } else {
        await createUserWhenLogin(authUser);
      }
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AuthAppUserKey, user?.sub],
      });
    },
  });
};

export const useGetLoggedInUserData = (): UseQueryResult<LoggedInUser> => {
  return useQuery({
    queryKey: [AppUsersKey],
    queryFn: () => getLoggedInUserData(),
  });
};

export const useUpdateProfileConfig = (
  userId: number,
  auth0Id?: string
): UseMutationResult<void, Error, { config: ProfileConfig }> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { config: ProfileConfig }) => {
      await updateProfileConfig(userId, data);
    },
    // Keep the byAuth0Id-keyed profile-config cache in sync after a save so
    // callers (including pages re-mounting later) don't read stale zones.
    // The mutation lives in two callers — Profile (current user) and the
    // user-edit page — invalidating in one place avoids each having to
    // remember to manually refetch. The user-edit page passes auth0Id; the
    // Profile page passes only userId, so we also bump the broader
    // [AppUsersKey, ...] family to refresh useGetCurrentUser.
    onSuccess: async () => {
      const invalidations: Promise<unknown>[] = [
        queryClient.invalidateQueries({ queryKey: [AppUsersKey] }),
        queryClient.invalidateQueries({ queryKey: [AppUsersGetOneKey, userId] }),
      ];
      if (auth0Id) {
        invalidations.push(
          queryClient.invalidateQueries({ queryKey: userProfileConfigQueryKey(auth0Id) })
        );
      }
      await Promise.all(invalidations);
    },
  });
};
